// Detector de atrito de UX:
// - Rage clicks: 3+ cliques no mesmo elemento em <1s (usuário frustrado).
// - Dead clicks: clique em elemento não-interativo seguido de inatividade
//   (URL não muda, DOM não muda, sem scroll/teclado/click novo) por 1s.

import { logUxEvent } from './uxLogger';

const RAGE_WINDOW_MS = 1000;
const RAGE_MIN_CLICKS = 3;
const DEAD_TIMEOUT_MS = 1000;

// Sequência de cliques mortos na mesma rota = paciente procurando um botão
// que não existe ali. Até 29/09/2026 isso só virava telemetria: a Rafaella
// deu 4 cliques mortos na triagem em 2 min (a própria resposta, o cabeçalho,
// a imagem do topo) e só então escreveu ao suporte dizendo que o botão de
// iniciar consulta não aparecia. Quem quiser reagir na tela assina abaixo.
const DEAD_STREAK_MIN = 2;
const DEAD_STREAK_WINDOW_MS = 30000;

const INTERACTIVE_TAGS = new Set(['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'LABEL', 'SUMMARY']);
const INTERACTIVE_ROLES = new Set(['button', 'link', 'checkbox', 'menuitem', 'option', 'radio', 'switch', 'tab', 'textbox']);

// Texto visível do elemento, ignorando <style>/<script> (evita seletores
// poluidos por CSS inline legado — auditoria UX 06/06).
const visibleText = (el) => {
  if (!el || el.nodeType !== 1) return '';
  if (el.tagName === 'STYLE' || el.tagName === 'SCRIPT' || el.tagName === 'NOSCRIPT') return '';
  let out = '';
  for (const node of el.childNodes) {
    if (node.nodeType === 3) out += node.textContent || '';
    else if (node.nodeType === 1) out += visibleText(node);
  }
  return out.trim().slice(0, 30);
};

// Constrói um seletor curto e estável para o elemento clicado.
// Evita IDs aleatórios (ex.: react-XYZ) — preferimos data-testid > tag+classe primária.
const describeElement = (el) => {
  if (!el || el.nodeType !== 1) return 'unknown';
  const tag = (el.tagName || 'div').toLowerCase();
  const id = el.getAttribute && el.getAttribute('id');
  const testId = el.getAttribute && (el.getAttribute('data-testid') || el.getAttribute('data-test'));
  if (testId) return `${tag}[data-testid="${testId}"]`;
  if (id && !/^react-|^radix-|^headlessui-/i.test(id)) return `${tag}#${id}`;
  let cls = '';
  if (el.classList && el.classList.length) {
    cls = '.' + Array.from(el.classList).slice(0, 2).join('.');
  }
  const text = visibleText(el);
  return `${tag}${cls}${text ? `:"${text}"` : ''}`;
};

const isInteractive = (el) => {
  let cur = el;
  for (let i = 0; cur && i < 5; i += 1) {
    if (cur.nodeType !== 1) { cur = cur.parentNode; continue; }
    if (INTERACTIVE_TAGS.has(cur.tagName)) return true;
    const role = cur.getAttribute && cur.getAttribute('role');
    if (role && INTERACTIVE_ROLES.has(role.toLowerCase())) return true;
    const tabindex = cur.getAttribute && cur.getAttribute('tabindex');
    if (tabindex !== null && tabindex !== undefined && tabindex !== '-1') return true;
    if (cur.onclick) return true;
    if (cur.style && cur.style.cursor === 'pointer') return true;
    cur = cur.parentNode;
  }
  return false;
};

let lastClicks = []; // { selector, ts, route }
let pendingDead = null; // { selector, route, timer }
let recentDeadClicks = []; // { route, ts }
const deadStreakSubscribers = new Set();

/**
 * Notifica quando o paciente acumula cliques mortos na mesma rota — sinal de
 * que ele não encontrou o caminho e está tateando a tela.
 *
 * @param {(info: { route: string, count: number }) => void} fn
 * @returns {() => void} cancela a inscrição
 */
export const subscribeDeadClickStreak = (fn) => {
  if (typeof fn !== 'function') return () => {};
  deadStreakSubscribers.add(fn);
  return () => deadStreakSubscribers.delete(fn);
};

const noteDeadClick = (route) => {
  const now = Date.now();
  recentDeadClicks = recentDeadClicks.filter(
    (d) => now - d.ts < DEAD_STREAK_WINDOW_MS && d.route === route
  );
  recentDeadClicks.push({ route, ts: now });
  if (recentDeadClicks.length < DEAD_STREAK_MIN) return;
  const count = recentDeadClicks.length;
  // Zera para não notificar a cada clique seguinte da mesma rajada.
  recentDeadClicks = [];
  deadStreakSubscribers.forEach((fn) => {
    try { fn({ route, count }); } catch {}
  });
};

const clearPendingDead = () => {
  if (pendingDead) {
    clearTimeout(pendingDead.timer);
    pendingDead = null;
  }
};

const onClick = (e) => {
  try {
    const el = e.target;
    const selector = describeElement(el);
    const route = window.location.pathname;
    const now = Date.now();

    // Rage detection.
    lastClicks = lastClicks.filter((c) => now - c.ts < RAGE_WINDOW_MS && c.route === route);
    lastClicks.push({ selector, ts: now, route });
    const sameSelectorCount = lastClicks.filter((c) => c.selector === selector).length;
    if (sameSelectorCount >= RAGE_MIN_CLICKS) {
      logUxEvent('rage_click', { selector, route, count: sameSelectorCount });
      lastClicks = lastClicks.filter((c) => c.selector !== selector);
    }

    // Dead-click detection: arma o timer só se o clique foi em algo NÃO interativo.
    clearPendingDead();
    if (!isInteractive(el)) {
      const baselineUrl = window.location.href;
      const baselineDomLength = document.body ? document.body.innerHTML.length : 0;
      const timer = setTimeout(() => {
        try {
          const urlChanged = window.location.href !== baselineUrl;
          const domChanged = document.body && Math.abs(document.body.innerHTML.length - baselineDomLength) > 200;
          if (!urlChanged && !domChanged) {
            logUxEvent('dead_click', { selector, route });
            noteDeadClick(route);
          }
        } catch {}
        pendingDead = null;
      }, DEAD_TIMEOUT_MS);
      pendingDead = { selector, route, timer };
    }
  } catch {
    // Silencioso por design.
  }
};

const onAnyActivity = () => clearPendingDead();

let initialized = false;
export const initFriction = () => {
  if (initialized || typeof document === 'undefined') return;
  initialized = true;
  document.addEventListener('click', onClick, true);
  document.addEventListener('keydown', onAnyActivity, true);
  document.addEventListener('scroll', onAnyActivity, { capture: true, passive: true });
};
